"use strict";

import Polygon from "ol/geom/Polygon";
import {unByKey} from "ol/Observable";

/**
 * @classdesc
 * OL-Implementierung der Digitalisierungs-Interaktion.
 *
 * @class
 * @extends gc.gis.module.GCDigitize.Impl
 * @memberof gc.gis.module.GCDigitize
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {Object} options Aufrufoptionen
 * @param {gc.gis.module.GCDigitize} options.instance Modul-Instanz
 * @param {gc.gis.map.GCMapView} [options.map={@link gc.gis.GCApplication#map}] Karten-Instanz
 */
gc.gis.module.GCDigitize.OlImpl = function(options)
{
	gc.gis.module.GCDigitize.Impl.call(this, options);

	this._helpTooltipSelectMsg = 'Klicken, um Objekt zu bearbeiten';
	this._helpTooltipSelectAndCopyMsg = 'Klicken, um Objekt auszuwählen';
	this._helpTooltipPointMsg = 'Klicken um Punkt hinzuzufügen';
	this._helpTooltipLineMsg = 'Klicken um Punkt hinzuzufügen,<br/>Doppelklick zum Beenden';
	this._helpTooltipPolygonMsg = 'Klicken um Punkt hinzuzufügen,<br/>Doppelklick zum Flächenschluss';
	this._helpTooltipPolygonClosedMsg = 'Klicken zum Abschließen der Fläche';
	this._helpTooltipRectMsg = 'Klicken um Startpunkt hinzuzufügen, dann Rechteck aufziehen,<br/>zum Beenden erneut klicken';
	this._helpTooltipCircleMsg = 'Klicken um Mittelpunkt hinzuzufügen, dann Kreis aufziehen,<br/>zum Beenden erneut klicken';
	this._helpTooltipNewVertexMsg = 'Zum <b>Einfügen</b> klicken.</br>Zum <b>Verschieben</b> klicken und ziehen.';
	this._helpTooltipEditVertexMsg = 'Zum <b>Verschieben</b> klicken und ziehen<br\>Zum <b>Löschen</b> nur klicken';

	/**
	 * @var ol.Map
	 */
	this.ol_map = this.map.getMap().getImpl();

	/**
	 * Interaktion zum Zeichnen
	 * @type {gc.gis.map.GCMapDrawInteraction}
	 */
	this._draw_interaction = gc.gis.iface.mapFactory.createMapDrawInteraction(this.map, {
		drawingStyle: this._generateStyleFunction(true),
		finalStyle: this._generateStyleFunction(false),
		allowClearDrawingArea: true
	});
	this._draw_interaction.setGeometryType(gc.gis.geom.GCGeometryType.POINT);
	this._draw_interaction.setActive(false);

	/**
	 * Trackinglayer mit den gezeichneten Objekten.
	 * @type {gc.gis.layer.GCTrackingLayer}
	 */
	this._draw_layer = this._draw_interaction.getLayer();

	/**
	 * Interaktion zum Bearbeiten
	 * @type {gc.gis.map.GCMapTransformInteraction}
	 */
	this._transform_interaction = gc.gis.iface.mapFactory.createMapTransformInteraction(this.map, {
		toolbarButtons: gc.gis.map.GCMapTransformInteractionButtons.CLEAR
			| gc.gis.map.GCMapTransformInteractionButtons.CANCEL,
		allowClearDrawingArea: true
	});
	this._transform_interaction.setActive(false);
	this._transform_interaction.setLayerFilter(this._draw_layer);

	/**
	 * Interaktion zum Auswählen von Geometrien
	 * @type {gc.gis.map.OLMapSelectInteraction}
	 */
	this._select_interaction = new gc.gis.map.OLMapSelectInteraction(this.map, {
		hoverStyle: gc.gis.iface.styleFactory.getStyleFunction(gc.gis.factory.StyleType.SELECTABLE)
	});
	this._select_interaction.setActive(false);

	this._updateHelptooltipText();
	this._initEvents();
};
gc.gis.module.GCDigitize.OlImpl.prototype = Object.create(gc.gis.module.GCDigitize.Impl.prototype);

/**
 * @inheritdoc
 */
gc.gis.module.GCDigitize.OlImpl.prototype.setDrawActive = function(state)
{
	if (state === true)
	{
		this.setTransformActive(false);
		this.setSelectActive(false);
	}
	this._draw_interaction.setActive(state === true);
	this._updateHelptooltipText();
};

/**
 * @inheritdoc
 */
gc.gis.module.GCDigitize.OlImpl.prototype.setTransformActive = function(state)
{
	if (state === true)
	{
		this.setDrawActive(false);
		this.setSelectActive(false);
	}
	this._transform_interaction.setActive(state === true);
	this._updateHelptooltipText();
};

/**
 * @inheritdoc
 */
gc.gis.module.GCDigitize.OlImpl.prototype.setSelectActive = function(state)
{
	if (state === true)
	{
		this.setDrawActive(false);
		this.setTransformActive(false);
	}
	this._select_interaction.setActive(state === true);
	this._updateHelptooltipText();
};

/**
 * @inheritDoc
 */
gc.gis.module.GCDigitize.OlImpl.prototype.getLayer = function ()
{
	return this._draw_layer;
};

/**
 * @inheritDoc
 */
gc.gis.module.GCDigitize.OlImpl.prototype.setGeometryType = function (geometryType)
{
	this._draw_interaction.setGeometryType(geometryType);
	this._updateHelptooltipText();
};

/**
 * @inheritDoc
 */
gc.gis.module.GCDigitize.OlImpl.prototype.enableFreehand = function (enable)
{
	this._draw_interaction.enableFreehand(enable);
};

/**
 * @inheritdoc
 */
gc.gis.module.GCDigitize.OlImpl.prototype.saveSuccess = function()
{
	// alle Features deselektieren und aus dem Trackinglayer entfernen, damit ein Depp nicht auf die Idee kommt er
	// könne nach dem Speichern die Objekte editieren
	this._transform_interaction.unSelectAllFeatures();
	this._draw_layer.clear();
};

/**
 * @inheritDoc
 */
gc.gis.module.GCDigitize.OlImpl.prototype.setSelectionLayers = function(layers)
{
	this._select_interaction.setLayers(layers);
};

/**
 * Initialisiert die Events.
 */
gc.gis.module.GCDigitize.OlImpl.prototype._initEvents = function() {
	var self = this;

	// Zeichnen-Aktionen aufmotzen
	var featureChangeEvtentKey,
		numPoints = -1,
		isSnappedOnFirst = false;
	this._draw_interaction.on(gc.gis.map.GCMapDrawInteractionEvent.DRAWSTART, this, function (evt) {
		try {
			var ol_feature = evt.feature.getImpl();
			featureChangeEvtentKey = ol_feature.on('change', function (evt2) {
				var ol_geom = evt2.target.getGeometry();

				if (ol_geom instanceof Polygon) {
					// das sketchPolygon ist stets geschlossen, daher die letzte Koordinate löschen
					var coords = ol_geom.getCoordinates()[0].slice(0, -1);

					// eine neue Koordinate wurde hinzugefügt
					if (numPoints !== coords.length) {
						numPoints = coords.length;
					}
					else if (numPoints > 1) {
						var firstPoint = coords[0];
						var lastPoint = coords[coords.length - 1];
						isSnappedOnFirst = (lastPoint[0] === firstPoint[0] && lastPoint[1] === firstPoint[1]);
					}

					self._updateHelptooltipText(isSnappedOnFirst);
					ol_feature.set('isSnappedOnFirst', isSnappedOnFirst, true);
				}
			});
		}
		catch (err) {
			gc.gis.iface.log.error(this, err);
		}

		// Event weiter triggern
		self._fireEvent(gc.gis.module.GCDigitizeImplEvent.DRAW_START, evt);
	});

	this._draw_interaction.on(gc.gis.map.GCMapDrawInteractionEvent.DRAWEND, this, function (evt) {
		try {
			unByKey(featureChangeEvtentKey);

			var ol_feature = evt.feature.getImpl();
			ol_feature.unset('isSnappedOnFirst', true);

			self._updateHelptooltipText();
		}
		catch (err) {
			gc.gis.iface.log.error(this, err);
		}

		// Event weiter triggern
		self._fireEvent(gc.gis.module.GCDigitizeImplEvent.DRAW_END, evt);
		self._draw_interaction.setActive(false);
		self._transform_interaction.setActive(true);
	});

	// Bearbeiten-Interaktion
	this._transform_interaction.on(gc.gis.map.GCMapTransformInteractionEvent.ENTER, this, function (evt) {
		if (!evt.selected)
		{
			self.map.showMouseTooltip(this._helpTooltipSelectMsg);
		}
	});
	this._transform_interaction.on(gc.gis.map.GCMapTransformInteractionEvent.REMOVEDRAWAREA, this, function (evt) {
		// Event wird von der Toolbar der TransformInteraction ausgelöst
		this.getLayer().removeFeature(evt.feature);
	});
	this._transform_interaction.on(gc.gis.map.GCMapTransformInteractionEvent.LEAVE, this, function (evt) {
		self.map.disableMouseTooltip();
	});
	this._transform_interaction.on(gc.gis.map.GCMapTransformInteractionEvent.SELECT, this, function (evt) {
		// Veranlasst das Neuzeichnen; erforderlich, da Stützpunkte ausgeblendet werden sollen
		// siehe Stylefunktion für fertige Objekte - Aufruf nodeMarker
		this.getLayer().getImpl().getSource().changed();

		self.map.disableMouseTooltip();
	});
	this._transform_interaction.on(gc.gis.map.GCMapTransformInteractionEvent.UNSELECT, this, function (evt) {
		// Veranlasst das Neuzeichnen; erforderlich, da Stützpunkte ausgeblendet werden sollen
		// siehe Stylefunktion für fertige Objekte - Aufruf nodeMarker
		this.getLayer().getImpl().getSource().changed();
	});
	this._transform_interaction.on(gc.gis.map.GCMapTransformInteractionEvent.HOVER_VERTEX, this, function (evt) {
		if (evt.isNew)
		{
			self.map.showMouseTooltip(self._helpTooltipNewVertexMsg);
		}
		else
		{
			self.map.showMouseTooltip(self._helpTooltipEditVertexMsg);
		}
	});
	this._transform_interaction.on(gc.gis.map.GCMapTransformInteractionEvent.LEAVE_VERTEX, this, function (evt) {
		self.map.disableMouseTooltip();
	});
	this._transform_interaction.on(gc.gis.map.GCMapTransformInteractionEvent.MODIFY_START, this, function (evt) {
		// console.debug('Stützpunktbearbeitung gestartet:');
		// console.debug(evt);
	});
	this._transform_interaction.on(gc.gis.map.GCMapTransformInteractionEvent.MODIFY_END, this, function (evt) {
		// console.debug('Stützpunktbearbeitung beendet:');
		// console.debug(evt);
	});
	this._transform_interaction.on(gc.gis.map.GCMapTransformInteractionEvent.TRANSLATING, this, function (evt) {
		// console.debug('Translation:');
		// console.debug(evt);
	});
	this._transform_interaction.on(gc.gis.map.GCMapTransformInteractionEvent.ROTATING, this, function (evt) {
		// console.debug('Rotation: '  + evt.angle * 180 / Math.PI);
		// console.debug(evt);
	});
	this._transform_interaction.on(gc.gis.map.GCMapTransformInteractionEvent.SCALING, this, function (evt) {
		// console.debug('Skalierung:');
		// console.debug(evt);
	});

	// Auswahl-Interaktion
	this._select_interaction.on(gc.gis.map.GCMapSelectInteractionEvent.ENTER, this, function (evt) {
			self.map.showMouseTooltip(self._helpTooltipSelectAndCopyMsg);
		}
	);
	this._select_interaction.on(gc.gis.map.GCMapSelectInteractionEvent.LEAVE, this, function (evt) {
			self.map.disableMouseTooltip();
		}
	);
	this._select_interaction.on(gc.gis.map.GCMapSelectInteractionEvent.CLICK, this, function (evt) {
			var id = evt.feature.getID(),
				feature_in_draw_layer = self._draw_layer.getFeatureByID(id),
				feature_clone;

			if (!feature_in_draw_layer)
			{
				feature_clone = evt.feature.clone();
				feature_clone.setID(id);
				self._draw_layer.addFeature(feature_clone);
			}
			// TODO Überwachung, ob ein Feature geändert wurde. Funktioniert leider nicht über die Transform-Interaktion, dort wird beim Deselektieren der Change-Status verworfen
			else if (feature_in_draw_layer/* && !self.wasChanged(feature_in_draw_layer)*/)
			{
				self._draw_layer.removeFeature(feature_in_draw_layer);
			}
		}
	);
};

/**
 * Erzeugt eine Funktion, welche die Styles bereitstellt.
 *
 * @protected
 * @method
 *
 * @param {boolean} drawStyle Flag ob Zeichenstyle oder finaler Style erzeugt werden sollen
 * @return {ol.StyleFunction}
 */
gc.gis.module.GCDigitize.OlImpl.prototype._generateStyleFunction = function(drawStyle)
{
	var self = this,
		func_draw = gc.gis.iface.styleFactory.getStyleFunction(gc.gis.factory.StyleType.DRAW),
		func_final = gc.gis.iface.styleFactory.getStyleFunction(gc.gis.factory.StyleType.DRAW_FINAL);

	// Style-Functions wrappen um Features anzureichern
	if (drawStyle)
	{
		return function(feature, resolution)
		{
			feature.set('finalGeometryType', self._draw_interaction.getGeometryType(), true);
			return func_draw(feature, resolution);
		}
	}

	return function(feature, resolution)
	{
		feature.set('selected',
			self._transform_interaction.isFeatureSelected(new gc.gis.feature.OLFeature(feature)), true);
		return func_final(feature, resolution);
	}
};

/**
 * Aktualisiert den Hilfe-Tooltip gemäß des aktuellen Geometrietyps. Bei Polygonen kann zudem noch angegeben werden,
 * ob diese geschlossen sind.
 *
 * @param polygonClosed
 * @private
 */
gc.gis.module.GCDigitize.OlImpl.prototype._updateHelptooltipText = function(polygonClosed)
{
	if (this._draw_interaction.isActive())
	{
		switch (this._draw_interaction.getGeometryType())
		{
			case gc.gis.geom.GCGeometryType.POLYGON:
				this.map.showMouseTooltip(
					polygonClosed ? this._helpTooltipPolygonClosedMsg : this._helpTooltipPolygonMsg
				);
				break;
			case gc.gis.geom.GCGeometryType.BOX:
			case gc.gis.geom.GCGeometryType.RECTANGLE:
				this.map.showMouseTooltip(this._helpTooltipRectMsg);
				break;
			case gc.gis.geom.GCGeometryType.CIRCLE:
				this.map.showMouseTooltip(this._helpTooltipCircleMsg);
				break;
			case gc.gis.geom.GCGeometryType.LINESTRING:
				this.map.showMouseTooltip(this._helpTooltipLineMsg);
				break;
			default:
				this.map.showMouseTooltip(this._helpTooltipPointMsg);
		}
	}
	else
	{
		this.map.disableMouseTooltip();
	}
};
